import type { AdaptedData, ChartComponentSpec, ChartSortOption, DataPoint } from "./types";

function pointValue(p: DataPoint): number {
  if (p == null) return Number.NaN;
  return typeof p === "number" ? p : p.y;
}

function categorySum(
  adapted: AdaptedData,
  index: number,
  includeQueryIds?: Set<string>,
): number {
  let sum = 0;
  let saw = false;
  for (const [queryId, s] of adapted.seriesDataMap) {
    if (includeQueryIds && !includeQueryIds.has(queryId)) continue;
    const v = pointValue(s.points[index]);
    if (!Number.isNaN(v)) {
      sum += v;
      saw = true;
    }
  }
  return saw ? sum : Number.NaN;
}

/** Build the set of queryIds whose values contribute to the sort key for value-* sorts.
 *  Returns undefined to mean "all series" (the legacy default). */
function resolveValueSortSeries(spec: ChartComponentSpec): Set<string> | undefined {
  const cfg = spec.widgetMutations?.sortConfig;
  if (!cfg?.sortSeriesQueryId) return undefined;
  const target = cfg.sortSeriesQueryId;
  const targetExists = spec.series.some((s) => s.queryId === target);
  if (!targetExists) return undefined;
  const ids = new Set<string>([target]);
  if (cfg.includeStackedInSort) {
    for (const s of spec.series) {
      if (s.stackOn === target) ids.add(s.queryId);
    }
  }
  return ids;
}

function reorder<T>(arr: T[], order: number[]): T[] {
  return order.map((i) => arr[i]);
}

export function applySort(adapted: AdaptedData, spec: ChartComponentSpec): AdaptedData {
  const cfg = spec.widgetMutations?.sortConfig;
  if (!cfg?.enabled) return adapted;

  const sortBy: ChartSortOption = cfg.sortBy;
  const n = adapted.categories.length;
  if (n === 0) return adapted;

  const order = Array.from({ length: n }, (_, i) => i);
  const direction = sortBy.endsWith("-asc") ? 1 : -1;

  if (sortBy.startsWith("value-")) {
    const includeQueryIds = resolveValueSortSeries(spec);
    const sums = order.map((i) => categorySum(adapted, i, includeQueryIds));
    order.sort((a, b) => {
      const va = sums[a];
      const vb = sums[b];
      // Push NaN/missing categories to the end regardless of direction.
      if (Number.isNaN(va) && Number.isNaN(vb)) return 0;
      if (Number.isNaN(va)) return 1;
      if (Number.isNaN(vb)) return -1;
      return (va - vb) * direction;
    });
  } else {
    // alpha-* and position-* both fall back to label-based string compare.
    // (For groupBy=position, the category label IS the position code, so position-*
    //  and alpha-* coincide. For groupBy=player, position-* needs a backend
    //  sort key on each datapoint to differentiate.)
    const keys = adapted.labels;
    order.sort((a, b) => keys[a].localeCompare(keys[b]) * direction);
  }

  return {
    categories: reorder(adapted.categories, order),
    labels: reorder(adapted.labels, order),
    seriesDataMap: new Map(
      Array.from(adapted.seriesDataMap, ([key, s]) => [
        key,
        { name: s.name, points: reorder(s.points, order) },
      ]),
    ),
  };
}
